import React from 'react';
import { TrendingUp, TrendingDown, CheckCircle, AlertCircle } from 'lucide-react';
import { Badge } from '../ui/badge';
import { calculateVariance, getVarianceDisplay } from '../../utils/lineItemHelpers';

interface VarianceBadgeProps {
  contractorCost: number | null | undefined;
  marketLow: number;
  marketHigh: number;
  assessment?: string | null;
  size?: 'sm' | 'md' | 'lg';
  showIcon?: boolean;
}

export const VarianceBadge: React.FC<VarianceBadgeProps> = ({
  contractorCost,
  marketLow,
  marketHigh,
  assessment,
  size = 'md',
  showIcon = true
}) => {
  const variance = calculateVariance(contractorCost, marketLow, marketHigh);
  const display = getVarianceDisplay(variance);

  // Size classes
  const sizeClasses = {
    sm: 'text-xs px-2.5 py-1',
    md: 'text-sm px-3 py-1.5',
    lg: 'text-base px-4 py-2'
  };

  const iconSizes = {
    sm: 'h-3.5 w-3.5',
    md: 'h-4 w-4',
    lg: 'h-5 w-5'
  };

  if (!display || variance === null) {
    return (
      <Badge variant="outline" className="text-gray-500 bg-gray-50 border-gray-200">
        No data
      </Badge>
    );
  }

  // When the backend assessment flags an issue but the numeric check says "in range",
  // use the backend assessment instead of showing a misleading green "In range" badge
  const assessmentLower = assessment?.toLowerCase();
  const backendFlaggedIssue = assessmentLower === 'suspiciously_low' || assessmentLower === 'overpriced' || assessmentLower === 'concern';

  if (variance === 0 && backendFlaggedIssue) {
    const isUnder = assessmentLower === 'suspiciously_low';
    const labelText = isUnder ? 'Below Market' : assessmentLower === 'overpriced' ? 'Above Market' : 'Concern';
    const badgeClass = isUnder
      ? 'bg-amber-50 text-amber-700 border-amber-300'
      : assessmentLower === 'overpriced'
        ? 'bg-red-50 text-red-700 border-red-300'
        : 'bg-amber-50 text-amber-700 border-amber-300';
    const IconOverride = isUnder ? TrendingDown : assessmentLower === 'overpriced' ? TrendingUp : AlertCircle;

    return (
      <div
        className={`${badgeClass} ${sizeClasses[size]} font-medium rounded-full inline-flex items-center gap-1.5 border`}
      >
        {showIcon && <IconOverride className={iconSizes[size]} />}
        <span>{labelText}</span>
      </div>
    );
  }

  // At this point, variance is guaranteed to be a number (not null)
  // Determine icon based on variance
  const Icon = variance === 0 ? CheckCircle :
               variance > 0 ? TrendingUp :
               TrendingDown;

  // Determine badge styling based on variance
  const getBadgeClass = () => {
    if (variance === 0) {
      return 'bg-green-50 text-green-700 border-green-200';
    }
    if (variance > 0) {
      // Over estimate
      return display.isSignificant
        ? 'bg-red-50 text-red-700 border-red-300'
        : 'bg-orange-50 text-orange-700 border-orange-200';
    }
    // Under estimate
    return display.isSignificant
      ? 'bg-amber-50 text-amber-700 border-amber-300'
      : 'bg-yellow-50 text-yellow-700 border-yellow-200';
  };

  return (
    <div
      className={`${getBadgeClass()} ${sizeClasses[size]} font-medium rounded-full inline-flex items-center gap-1.5 border`}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      <span>{display.text}</span>
      {assessment && size !== 'sm' && (
        <span className="ml-1 opacity-75 font-normal">
          • {assessment}
        </span>
      )}
    </div>
  );
};
